import { gql } from 'graphql-tag';

const companyMetadataFragment = gql`
  fragment CompanyMetadata on Company {
    company_name
    feature_flags
    is_shipper
    is_carrier
    is_pilot
    is_paying_customer
    is_test

    fms_integrated_at
    isPendingSignup
    signupCompletedAt
    vatNumbers {
      vatNumber
    }

    fieldDataSources {
      companyName
      isShipper
      isCarrier
      isTest
    }

    companyIdentifiers {
      companyIdentifierId
      discriminatorCountry
      identifierType
      identifierValue
      source
      companyId
    }

    tags {
      name
    }
  }
`;

export const companiesFilterOptionsQuery = gql`
  query CompaniesFilterOptions {
    availableCompanyTags {
      name
    }
    featureFlagMetadata(applies_to: "company") {
      feature_flag_id
      display_name
    }
  }
`;

export const companiesQuery = gql`
  ${companyMetadataFragment}

  query CompanyList(
    $limit: Int
    $cursor: String
    $type: CompanyTypeInput
    $textSearch: String
    $tagName: String
    $signupStatus: UnifiedCompanySignupStatus
    $featureFlags: [String!]
    $showOnlyOnboardingRolledBack: Boolean
    $agreementStatuses: [CompanyAgreementStatus!]
  ) {
    companies(
      first: $limit
      after: $cursor
      type: $type
      textSearch: $textSearch
      tagName: $tagName
      signupStatus: $signupStatus
      featureFlags: $featureFlags
      showOnlyOnboardingRolledBack: $showOnlyOnboardingRolledBack
      agreementStatuses: $agreementStatuses
    ) {
      edges {
        node {
          company_id
          ...CompanyMetadata

          agreement {
            hasAgreement
            agreementVersion
          }

          shipperToursCount
          carrierToursCount
          totalVehiclesCount

          unifiedSignupStatus
          signupStatusTransitionedAt
          onboardingAction {
            type
          }

          platforms {
            platform_id
            platform_name
            company_id_on_platform
          }

          companyIdentifiers {
            companyIdentifierId
            identifierType
            identifierValue
          }
        }
      }

      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const apiEndpointUsageLogs = gql`
  query ApiEndpointUsageLogs(
    $first: Int
    $cursor: String
    $endpoint: String
    $implementationStatus: ImplementationStatus
    $apiHealth: ApiHealth
  ) {
    apiEndpointUsageLogs(
      first: $first
      after: $cursor
      endpoint: $endpoint
      implementationStatus: $implementationStatus
      apiHealth: $apiHealth
    ) {
      edges {
        node {
          apiImplementedAt
          companyId
          companyName
          endpointId
          endpointName
          integrationIdentifier
          firstCallAt
          lastCallAt
          counters {
            logDate
            statusCode
            noOfCalls
          }
          webhookSubscription {
            status
            subscriptionId
            companyId
          }
          implementationStatus
          apiHealthStatus
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const apiEndpointUsageLogsReportDownloadURIQuery = gql`
  query ApiEndpointUsageLogsReportDownloadURI(
    $endpoint: String
    $implementationStatus: String
    $apiHealth: String
    $companyId: String
  ) {
    apiEndpointUsageLogsReportDownloadURI(
      endpoint: $endpoint
      implementationStatus: $implementationStatus
      apiHealth: $apiHealth
      companyId: $companyId
    )
  }
`;

export const companyQuery = gql`
  ${companyMetadataFragment}

  query CompanyDetails($company_id: String!) {
    availableCompanyTags {
      name
    }

    company(company_id: $company_id) {
      company_id
      ...CompanyMetadata
      platforms {
        company_platform_id
        platform_id
        platform_name
        company_id_on_platform
        tpaProductionUrl: platformPageUrl(pageType: tpa_production_company_page)
        tpaTestUrl: platformPageUrl(pageType: tpa_test_company_page)
        tpaIntUrl: platformPageUrl(pageType: tpa_int_company_page)
        tpaDevUrl: platformPageUrl(pageType: tpa_dev_company_page)
      }
    }
  }
`;

export const companyTabCountsQuery = gql`
  query CompanyTabCounts($company_id: String!) {
    company(company_id: $company_id) {
      company_id
      totalVehiclesCount
      users {
        count
      }
      carriersConnection(relationshipTypes: [CARRIER, SUBCARRIER]) {
        count
      }
      customersConnection {
        count
      }
    }
    telematicsIntegrations(companyId: $company_id) {
      count
    }
  }
`;

export const companyAgreementQuery = gql`
  query CompanyAgreement($company_id: String!) {
    company(company_id: $company_id) {
      company_id
      agreement {
        hasAgreement
        contractualPartner
        agreementVersion
        signedDate
        updatedAt
      }
    }
  }
`;

export const companyFeatureFlagsMetadataQuery = gql`
  query CompanyWithFeatureFlagMetadata($company_id: String!, $applies_to: String!, $first: Int, $cursor: String) {
    company(company_id: $company_id) {
      company_id
      company_name
      feature_flags
      agreement {
        hasAgreement
        agreementVersion
      }
      featureFlagAuditLogConnection(first: $first, after: $cursor) {
        edges {
          node {
            featureFlagAuditLogId
            featureFlagId
            user {
              user_id
              first_name
              last_name
            }
            createdAt
            metadata {
              ... on FlagCreatedMetadata {
                action
                value
              }
              ... on FlagUpdatedMetadata {
                action
                oldValue
                newValue
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }

    featureFlagMetadata(applies_to: $applies_to) {
      feature_flag_id
      display_name
      description
      usage
      domains
    }
  }
`;

export const updateCompanyMutation = gql`
  ${companyMetadataFragment}

  mutation UpdateCompany($company_id: String!, $input: UpdateCompanyInput) {
    updateCompany(company_id: $company_id, input: $input) {
      company_id
      ...CompanyMetadata

      assets {
        consigneeLogo {
          url
        }

        consigneeBackground {
          url
        }
      }
    }
  }
`;

export const updateCompanyFeatureFlagMutation = gql`
  mutation UpdateCompanyFeatureFlag($companyId: String!, $featureFlag: String!, $value: Boolean) {
    updateCompanyFeatureFlag(companyId: $companyId, featureFlag: $featureFlag, value: $value)
  }
`;

export const createCompanyIdentifierMutation = gql`
  mutation CreateCompanyIdentifier($input: CreateCompanyIdentifierInput!) {
    createCompanyIdentifier(input: $input) {
      success
    }
  }
`;

export const deleteCompanyIdentifierMutation = gql`
  mutation DeleteCompanyIdentifier($input: DeleteCompanyIdentifierInput!) {
    deleteCompanyIdentifier(input: $input) {
      success
    }
  }
`;

export const createVehiclesMutation = gql`
  mutation CreateVehicles($input: CreateVehiclesInput!) {
    createVehicles(input: $input)
  }
`;

export const createCompanyMutation = gql`
  ${companyMetadataFragment}

  mutation CreateCompany($input: CreateCompanyInput) {
    createCompany(input: $input) {
      company_id
      ...CompanyMetadata

      shipperToursCount
      carrierToursCount
      totalVehiclesCount

      unifiedSignupStatus

      platforms {
        platform_id
        platform_name
        company_id_on_platform
      }
    }
  }
`;

export const companyShipperToursByCarrierMetricsQuery = gql`
  query CompanyShipperToursByCarriersMetrics($company_id: String!, $first: Int, $cursor: String) {
    company(company_id: $company_id) {
      company_id

      tourReportsByCarrier(first: $first, after: $cursor) {
        edges {
          node {
            carrier {
              company_id
              company_name
            }

            toursCount
            visibilityIndex
            allocatedToursCount
          }
        }

        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const companyCarrierToursByShipperMetricsQuery = gql`
  query CompanyCarrierToursByShippersMetrics($company_id: String!, $first: Int, $cursor: String) {
    company(company_id: $company_id) {
      company_id

      tourReportsByShipper(first: $first, after: $cursor) {
        edges {
          node {
            shipper {
              company_id
              company_name
            }

            toursCount
            visibilityIndex
            allocatedToursCount
          }
        }

        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const companyIntegrationsListQuery = gql`
  query CompanyIntegrationsList($company_id: String!) {
    company(company_id: $company_id) {
      company_id
      company_name
      unifiedSignupStatus
      shipperToursCount
      carrierToursCount
      apiEndpointUsageLogs {
        apiImplementedAt
        endpointId
        endpointName
        integrationIdentifier
        firstCallAt
        lastCallAt
        counters {
          logDate
          statusCode
          noOfCalls
        }
        webhookSubscription {
          status
          subscriptionId
          companyId
        }
        implementationStatus
        apiHealthStatus
      }
    }
  }
`;

export const companyWebhookSubscriptionsQuery = gql`
  query CompanyWebhookSubscriptions($company_id: String!) {
    company(company_id: $company_id) {
      company_id
      companyWebhookSubscriptions {
        subscriptionId
        status
        url
        topics
        lastStatusChange
        updatedAt
      }
    }
  }
`;

export const companyAssetsQuery = gql`
  query CompanyAssets($company_id: String!) {
    company(company_id: $company_id) {
      company_id
      assets {
        consigneeLogo {
          url
        }

        consigneeBackground {
          url
        }
      }
    }
  }
`;

export const companyOnboardingQuery = gql`
  query CompanyOnboarding($company_id: String!) {
    company(company_id: $company_id) {
      company_id
      unifiedSignupStatus
      signupStatusTransitionedAt
      onboardingAction {
        type
      }
      unifiedSignupEvents {
        type
        eventTime
        visibilitySource
        triggeredByUser {
          user_id
          first_name
          last_name
        }
      }

      signupVisibilitySources {
        status
        type
      }

      signupVisibilitySourcesViaSubcarriers {
        status
        type
      }
    }
  }
`;

export const companyInvitationsQuery = gql`
  query CompanyInvitations($company_id: String!) {
    companyInvitations(company_id: $company_id) {
      companyInviteId
      invitedAt
      companyName
      status
      inviteMethod
      historyLog {
        time
        type
      }
      invitedByCompany {
        company_id
        company_name
      }
      invitedByUser {
        first_name
        last_name
      }
      recipients {
        email
        locale
      }
    }
  }
`;

export const updateCompanySignupStatusMutation = gql`
  mutation UpdateCompanySignupStatus($company_id: String!, $input: UnifiedCompanySignupStatusUpdateInput!) {
    updateUnifiedCompanySignupStatus(companyId: $company_id, input: $input)
  }
`;

export const companyDropdownQuery = gql`
  ${companyMetadataFragment}

  query CompanyDropdown($companyId: String!) {
    company(company_id: $companyId) {
      company_id
      ...CompanyMetadata
    }
  }
`;

export const companiesDropdownListQuery = gql`
  ${companyMetadataFragment}

  query CompaniesDropdownList($textSearch: String, $type: CompanyTypeInput) {
    companies(first: 100, textSearch: $textSearch, type: $type) {
      edges {
        node {
          company_id
          ...CompanyMetadata
        }
      }
    }
  }
`;

export const companyConfigurationQuery = gql`
  query CompanyConfiguration($company_id: String!) {
    company(company_id: $company_id) {
      company_id
      configuration {
        configurations {
          customerPortal {
            primaryTransportIdentifier
            detailViewTransportIdentifiers
          }
          sharedTourView {
            visibleTransportIdentifiers
          }
          sharedStopView {
            visibleTransportIdentifiers
          }
          customFields {
            fields {
              key
              displayName
              visibility
              visibilityAction
            }
          }
        }
      }
      settings {
        allocationPreferTractorOverTrailer
        tourMinAbandonTimeHours
        tourMaxAbandonTimeHours
      }
    }
  }
`;

export const updateCompanyConfigurationMutation = gql`
  mutation UpdateCompanyConfiguration($input: UpdateCompanyConfigurationInput!) {
    updateCompanyConfiguration(input: $input) {
      company_id
      configuration {
        configurations {
          customerPortal {
            primaryTransportIdentifier
            detailViewTransportIdentifiers
          }
          sharedTourView {
            visibleTransportIdentifiers
          }
          sharedStopView {
            visibleTransportIdentifiers
          }
          customFields {
            fields {
              visibilityAction
              visibility
              displayName
              key
            }
          }
        }
      }
    }
  }
`;

export const updateCompanySettingMutation = gql`
  mutation UpdateCompanySetting($input: UpdateCompanySettingsInput!) {
    updateCompanySettings(input: $input) {
      company_id
      settings {
        allocationPreferTractorOverTrailer
        tourMinAbandonTimeHours
        tourMaxAbandonTimeHours
      }
    }
  }
`;

export const telematicsIntegrationSensitiveConnectionDataQuery = gql`
  query SensitiveConnectionData($telematics_integration_id: String!, $captcha_token: String!) {
    sensitiveConnectionData(telematics_integration_id: $telematics_integration_id, captcha_token: $captcha_token)
  }
`;

export const telematicsIntegrationSensitiveDataQuery = gql`
  query SensitiveData($telematics_integration_id: String!, $captcha_token: String!) {
    sensitiveData(telematics_integration_id: $telematics_integration_id, captcha_token: $captcha_token) {
      connectionData
      syncStateData
    }
  }
`;

export const companyDashboardsQuery = gql`
  query CompanyDashboards($company_id: String!) {
    company(company_id: $company_id) {
      company_id
      lightdashEmbeddings {
        allocationSourceAndSuccessUrl
      }
    }
  }
`;

export const updateCompanyWebhookSubscription = gql`
  mutation UpdateCompanyWebhookSubscription($input: UpdateCompanyWebhookSubscriptionInput!) {
    updateCompanyWebhookSubscription(input: $input)
  }
`;

export const apiEndpointUsageLogsEndpointsQuery = gql`
  query ApiEndpointUsageLogsEndpoints {
    apiEndpointUsageLogsEndpoints {
      endpointId
      endpointName
    }
  }
`;

export const companyTFSSpaceQuery = gql`
  query CompanyTFSSpace($company_id: String!) {
    company(company_id: $company_id) {
      company_id
      company_name
      tfsSpace {
        spaceId
        rootFolderId
        spaceName
        region
        accountId
        createdAt
      }
    }
  }
`;

export const provisionTFSSpaceMutation = gql`
  mutation ProvisionTFSSpace($input: ProvisionCompanyTFSSpaceInput!) {
    provisionTFSSpace(input: $input) {
      spaceId
      rootFolderId
      spaceName
      region
      accountId
      createdAt
    }
  }
`;

const lemLicenseFields = gql`
  fragment LemLicenseFields on CompanyLemLicenses {
    company_id
    transporeon_account_id
    arc_license_active
    arc_toggle_status
    licenses {
      license_id
      plan_id
      plan_name
      status
      status_raw
      created_at
    }
  }
`;

export const companyLemLicensesQuery = gql`
  query CompanyLemLicenses($company_id: String!) {
    companyLemLicenses(company_id: $company_id) {
      ...LemLicenseFields
    }
  }
  ${lemLicenseFields}
`;

export const grantCompanyLemLicenseMutation = gql`
  mutation GrantCompanyLemLicense($company_id: String!) {
    grantCompanyLemLicense(company_id: $company_id) {
      ...LemLicenseFields
    }
  }
  ${lemLicenseFields}
`;

export const revokeCompanyLemLicenseMutation = gql`
  mutation RevokeCompanyLemLicense($company_id: String!) {
    revokeCompanyLemLicense(company_id: $company_id) {
      ...LemLicenseFields
    }
  }
  ${lemLicenseFields}
`;

export const companyApiMetricsMessages = gql`
  query CompanyApiMetricsMessages(
    $companyId: String!
    $endpointId: String!
    $statusCode: String
    $startDate: DateTime!
    $endDate: DateTime!
    $first: Int
    $cursor: String
    $searchTerm: String
    $sortDirection: SortDirection
  ) {
    company(company_id: $companyId) {
      company_id
      apiMetricsMessages(
        endpointId: $endpointId
        statusCode: $statusCode
        startDate: $startDate
        endDate: $endDate
        first: $first
        after: $cursor
        searchTerm: $searchTerm
        sortDirection: $sortDirection
      ) {
        edges {
          node {
            endpointId
            errorMessage
            statusCode
            requestPayload
            requestUrl
            timestamp
          }
          cursor
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

export const adminApiMetricsMessages = gql`
  query AdminApiMetricsMessages(
    $companyId: String!
    $endpointId: String!
    $statusCode: String
    $startDate: DateTime!
    $endDate: DateTime!
    $first: Int
    $cursor: String
    $searchTerm: String
    $sortDirection: SortDirection
  ) {
    apiMetricsMessages(
      companyId: $companyId
      endpointId: $endpointId
      statusCode: $statusCode
      startDate: $startDate
      endDate: $endDate
      first: $first
      after: $cursor
      searchTerm: $searchTerm
      sortDirection: $sortDirection
    ) {
      edges {
        node {
          endpointId
          errorMessage
          statusCode
          requestPayload
          requestUrl
          timestamp
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;
